import { useEffect, useState } from 'react';
import { getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';

// RTP by stake band and by player, for one PvH game (Phase 3, 2026-09-15).
//
// The view the platform never had. Every other analytics surface aggregates to
// game x time, which is precisely the shape that averages away a give-back
// flowing to the smallest bets — a defect that has actually occurred here.
//
// Cells that cannot support a claim say "measuring" instead of showing a number.
// That is not timidity: cut a small book into bands and a band holding 13 drops
// will happily read "52% take" against a 5% dial from variance alone, and a panel
// that prints that gets a healthy game re-tuned. The +/- figure beside each RTP is
// a ratio-estimator band at 3 sigma; when it is wider than the distance to target,
// the cell is not telling you anything yet.

type Cell = {
  bets: number; turnover: number; payout: number;
  rtp: number | null; takePct: number | null; sePts: number | null;
  measuring: boolean; why: string | null;
};
type Band = Cell & { band: number; label: string };
type UserRow = Cell & { userId: number; username: string };
type Data = {
  game: string; days: number; grain: string; includeTest: boolean;
  thresholds: { minBets: number; z: number };
  total: Cell; byBand: Band[]; byUser: UserRow[]; note: string | null;
};

const inr = (n: number) => `₹${(n | 0).toLocaleString('en-IN')}`;

async function api(url: string) {
  const t = getAdminAccessToken();
  const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...(t ? { authorization: `Bearer ${t}` } : {}) } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error?.message || 'API error');
  return res.json();
}

function Verdict({ c, target }: { c: Cell; target: number | null }) {
  if (c.measuring) {
    return <span className="text-sky-600 dark:text-sky-400" title={c.why || ''}>measuring</span>;
  }
  // "Off target" only when the gap EXCEEDS this cell's own noise band. Anything
  // inside it is sampling noise wearing a number's clothes.
  const off = target != null && c.takePct != null && c.sePts != null
    && Math.abs(c.takePct - target) > c.sePts;
  return (
    <span className={off ? 'text-amber-700 dark:text-amber-400 font-semibold' : 'text-emerald-700 dark:text-emerald-400'}>
      {c.rtp}% <span className="opacity-60 font-normal">RTP</span>
      <span className="opacity-70"> · take {c.takePct}% ±{c.sePts}pt</span>
    </span>
  );
}

export default function RtpTab() {
  const [games, setGames] = useState<string[]>([]);
  const [game, setGame] = useState('plinko');
  const [days, setDays] = useState(30);
  // null until the server's testing-phase default is known (Home → "Testing phase" bar);
  // the request then always carries an EXPLICIT include_test so the box is never a lie.
  const [includeTest, setIncludeTest] = useState<boolean | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api('/api/admin/reports/rtp-games')
      .then((r) => setGames(r?.data?.games || []))
      .catch(() => setGames([]));
    api('/api/admin/reports/test-mode')
      .then((r) => setIncludeTest(!!r?.data?.includeTest))
      .catch(() => setIncludeTest(false));
  }, []);

  useEffect(() => {
    if (includeTest === null) return;
    let dead = false;
    setLoading(true); setErr('');
    Promise.all([
      api(`/api/admin/reports/rtp-breakdown?game=${game}&days=${days}&include_test=${includeTest ? 'true' : 'false'}`),
      api(`/api/admin/reports/target-profit`).catch(() => null),
    ]).then(([r, tp]) => {
      if (dead) return;
      setData(r?.data || null);
      const g = tp?.games?.find((x: { game: string }) => x.game === game);
      setTarget(g && g.targetPct != null ? Number(g.targetPct) : null);
    }).catch((e) => !dead && setErr(e.message))
      .finally(() => !dead && setLoading(false));
    return () => { dead = true; };
  }, [game, days, includeTest]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select value={game} onChange={(e) => setGame(e.target.value)}
                className="rounded border px-2 py-1 bg-white dark:bg-gray-900">
          {(games.length ? games : [game]).map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                className="rounded border px-2 py-1 bg-white dark:bg-gray-900">
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>last {d} days</option>)}
        </select>
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={!!includeTest} disabled={includeTest === null} onChange={(e) => setIncludeTest(e.target.checked)} />
          include test accounts
        </label>
        {target != null && <span className="text-xs opacity-70">target take {target}%</span>}
        {loading && <span className="text-xs opacity-60">loading…</span>}
      </div>

      {err && <div className="text-sm text-rose-600">{err}</div>}

      {data && (
        <>
          {data.note && (
            <div className="rounded border border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 p-2 text-xs">
              {data.note}
            </div>
          )}

          <div className="text-xs opacity-70">
            Grain: {data.grain}. A cell needs {data.thresholds.minBets} settled bets before an RTP is
            stated; below that it reads <span className="text-sky-600 dark:text-sky-400">measuring</span>.
            The ± figure is this cell&apos;s own sampling noise at {data.thresholds.z}σ — a gap to target
            smaller than it is not a finding.
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left bg-gray-50 dark:bg-gray-800/60">
                  <th className="p-2">Stake band</th>
                  <th className="p-2 text-right">Bets</th>
                  <th className="p-2 text-right">Turnover</th>
                  <th className="p-2 text-right">Paid out</th>
                  <th className="p-2">Return to player</th>
                </tr>
              </thead>
              <tbody>
                {data.byBand.filter((b) => b.bets > 0).map((b) => (
                  <tr key={b.band} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="p-2 font-medium whitespace-nowrap">{b.label}</td>
                    <td className="p-2 text-right">{b.bets}</td>
                    <td className="p-2 text-right">{inr(b.turnover)}</td>
                    <td className="p-2 text-right">{inr(b.payout)}</td>
                    <td className="p-2"><Verdict c={b} target={target} /></td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 dark:border-gray-700 font-bold">
                  <td className="p-2">ALL</td>
                  <td className="p-2 text-right">{data.total.bets}</td>
                  <td className="p-2 text-right">{inr(data.total.turnover)}</td>
                  <td className="p-2 text-right">{inr(data.total.payout)}</td>
                  <td className="p-2"><Verdict c={data.total} target={target} /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-sm font-bold pt-2">By player <span className="font-normal opacity-60">(top {data.byUser.length} by turnover)</span></h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left bg-gray-50 dark:bg-gray-800/60">
                  <th className="p-2">Player</th>
                  <th className="p-2 text-right">Bets</th>
                  <th className="p-2 text-right">Turnover</th>
                  <th className="p-2 text-right">Paid out</th>
                  <th className="p-2">Return to player</th>
                </tr>
              </thead>
              <tbody>
                {data.byUser.map((u) => (
                  <tr key={u.userId} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="p-2 whitespace-nowrap">{u.username} <span className="opacity-50">#{u.userId}</span></td>
                    <td className="p-2 text-right">{u.bets}</td>
                    <td className="p-2 text-right">{inr(u.turnover)}</td>
                    <td className="p-2 text-right">{inr(u.payout)}</td>
                    <td className="p-2"><Verdict c={u} target={target} /></td>
                  </tr>
                ))}
                {!data.byUser.length && (
                  <tr><td className="p-2 opacity-60" colSpan={5}>No settled play in this window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
